import type { GeographicMarketTimeSeries as MarketDataTimeSeries } from '@einwert/valuation-api-client'
import type { Percentage, Period, TopicType, ValuationDataPoint as ValuationDataPointSchema } from '@einwert/valuation-api-client'
import { TimeseriesValue, TimeSeriesValueType } from 'ClientPortal/src/api/valuationModels'
import {
    assembleAssetTitle,
    ExhaustivenessError,
    isoDateStringToLocalizedMonthYear,
    isoDateStringToLocalFormat,
    LineData,
    MonetaryValue,
    MonetaryValueDataObjectType,
    MoneyPerAreaValue,
} from 'ComponentLibrary'
import { AssetLocation } from 'ClientPortal/src/pages/MarketData/types'
import { getTimeSeriesStatsTypeLabels, TimeSeriesStatsTypeColor } from 'ClientPortal/src/pages/MarketData/constants'
import { useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { useTranslation } from 'react-i18next'

export const useMarketDataAdapter = () => {
    const theme = useTheme()
    const { t } = useTranslation('pages', { keyPrefix: 'marketData' })
    const locale = useCurrentResolvedLocale()
    const statsTypeLabels = getTimeSeriesStatsTypeLabels(t)

    // This method should not be needed once we include this data in the backend response.
    const mapAssetToMarketDataSeries = ({ topic, asset }: { topic: TopicType; asset: AssetLocation }): MarketDataTimeSeries[] => {
        if (!asset.uuid) {
            return []
        }

        const defaults = {
            title: assembleAssetTitle({
                ...asset.address,
                customer_asset_reference: asset.customer_asset_reference,
            }),
            region: { name: asset.address.city },
            topic,
            period: 'day' as const,
        }

        const mapValues = <T extends number | MonetaryValue | MoneyPerAreaValue>(fn: (point: ValuationDataPointSchema) => T | undefined) =>
            (asset.history
                ?.map((point) => ({ date: point.valuation_date, value: fn(point) }))
                .filter((point) => point.value !== undefined) as TimeseriesValue<T>[]) ?? []

        switch (topic) {
            case 'price_single_family_house':
            case 'price_townhouse_new':
            case 'price_townhouse_existing':
                return [
                    {
                        ...defaults,
                        value_type: 'monetary_value',
                        description: t('descriptions.marketValue'),
                        values: mapValues<MonetaryValue>((point) => MonetaryValue.fromMonetaryValueDataObject(point.market_value)),
                    },
                ]
            case 'price_apartment_existing':
            case 'price_apartment_new':
                if (!asset.valuation.rentable_area?.value && !asset.valuation?.living_area?.value) {
                    return []
                }
                return [
                    {
                        ...defaults,
                        value_type: 'money_per_area_value',
                        description: t('descriptions.marketValuePerArea', {
                            areaLabel: asset.valuation.rentable_area?.value ? t('descriptions.rentableArea') : t('descriptions.livingArea'),
                        }),
                        values: mapValues<MoneyPerAreaValue>((point) =>
                            MonetaryValue.fromMonetaryValueDataObject(point.market_value)
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                .divide(asset.valuation.rentable_area?.value || asset.valuation.living_area!.value)
                                .toMoneyPerAreaValue()
                        ),
                    },
                ]
            case 'price_plot_single_family_house':
                if (!asset.valuation?.land_area?.value) {
                    return []
                }
                return [
                    {
                        ...defaults,
                        value_type: 'money_per_area_value',
                        description: t('descriptions.landValuePerArea'),
                        values: mapValues<MoneyPerAreaValue>((point) =>
                            point.land_value
                                ? MonetaryValue.fromMonetaryValueDataObject(point.land_value)
                                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                      .divide(asset.valuation.land_area!.value)
                                      .toMoneyPerAreaValue()
                                : undefined
                        ),
                    },
                ]
            case 'rent_apartment_existing':
            case 'rent_apartment_new':
                if (!asset.valuation.rentable_area?.value && !asset.valuation.living_area?.value) {
                    return []
                }
                return [
                    {
                        ...defaults,
                        value_type: 'money_per_area_value',
                        description: t('descriptions.monthlyMarketRentPerArea', {
                            areaLabel: asset.valuation.rentable_area?.value ? t('descriptions.rentableArea') : t('descriptions.livingArea'),
                        }),
                        values: mapValues<MoneyPerAreaValue>((point) =>
                            point.rent?.income
                                ? MonetaryValue.fromMonetaryValueDataObject(point.rent?.income)
                                      .divide(12) // Yearly to monthly
                                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                      .divide(asset.valuation.rentable_area?.value || asset.valuation.living_area!.value)
                                      .toMoneyPerAreaValue()
                                : undefined
                        ),
                    },
                ]
            case 'retail_rent_city':
            case 'retail_rent_1b':
            case 'retail_rent_secondary':
            case 'office_rent_city':
            case 'office_rent_fringe':
            case 'office_rent_suburb':
            case 'office_rent_agglomeration':
            case 'logistics_rent_logistics':
            case 'logistics_rent_service':
                if (!asset.valuation.rentable_area?.value && !asset.valuation.usage_area?.value) {
                    return []
                }
                return [
                    {
                        ...defaults,
                        value_type: 'money_per_area_value',
                        description: t('descriptions.monthlyMarketRentPerArea', {
                            areaLabel: asset.valuation.rentable_area?.value ? t('descriptions.rentableArea') : t('descriptions.usableArea'),
                        }),
                        values: mapValues<MoneyPerAreaValue>((point) =>
                            point.rent?.income
                                ? MonetaryValue.fromMonetaryValueDataObject(point.rent?.income)
                                      .divide(12) // Yearly to monthly
                                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                      .divide(asset.valuation.rentable_area?.value || asset.valuation.usage_area!.value)
                                      .toMoneyPerAreaValue()
                                : undefined
                        ),
                    },
                ]
            case 'logistics_yield_logistics':
            case 'logistics_yield_industrial':
            case 'logistics_yield_logistics_prime':
            case 'logistics_yield_industrial_prime':
            case 'office_yield_decentral':
            case 'office_yield_central':
            case 'retail_yield_decentral':
            case 'retail_yield_central':
                // WEB-2773 Add yield data to the asset history
                return []
            case 'gross_domestic_product':
            case 'gross_domestic_product_ranking':
            case 'employment':
            case 'unemployment':
            case 'unemployment_ranking':
            case 'population':
            case 'building_permit_apartments':
            case 'area':
            case 'population_density':
            case 'population_ranking':
            default:
                return []
        }
    }

    const formatDate = (date: string, period: Period): string => {
        const localizedDate = dayjs(date).locale(locale)

        switch (period) {
            case 'year':
                return localizedDate.format('YYYY')
            case 'quarter':
                return localizedDate.format('YYYY [Q]Q')
            case 'month':
                return isoDateStringToLocalizedMonthYear(date, 'short', locale) ?? ''
            case 'week':
                return localizedDate.format(locale === 'en-US' ? '[CW] ww/YYYY' : '[KW] ww/YYYY')
            case 'day':
                return isoDateStringToLocalFormat(date, false, locale) ?? ''
            default:
                throw ExhaustivenessError(period)
        }
    }

    const mapTimeSeriesToChartData = (timeSeries: MarketDataTimeSeries[]): LineData[] => {
        const minSeries = timeSeries.find((ts) => ts.stats_type === 'minimum')
        const maxSeries = timeSeries.find((ts) => ts.stats_type === 'maximum')

        return timeSeries.map((singleTimeSeries) => {
            const fallbackColor = singleTimeSeries.origin ? theme.palette.customtext.black38 : theme.palette.chartColors.blue
            const color = singleTimeSeries.stats_type ? TimeSeriesStatsTypeColor[singleTimeSeries.stats_type] : fallbackColor

            return {
                id: singleTimeSeries.stats_type ? statsTypeLabels[singleTimeSeries.stats_type] : singleTimeSeries.title,
                description: singleTimeSeries.description,
                data: singleTimeSeries.values.map(({ value, date }, index) => ({
                    value: mapValueToNumber(singleTimeSeries.value_type, value),
                    dateFormatted: formatDate(date, singleTimeSeries.period),
                    date,
                    range:
                        singleTimeSeries.stats_type === 'average' &&
                        minSeries?.values.at(index) !== undefined &&
                        maxSeries?.values.at(index) !== undefined
                            ? {
                                  min: mapValueToNumber(minSeries.value_type, minSeries.values[index].value),
                                  max: mapValueToNumber(maxSeries.value_type, maxSeries.values[index].value),
                              }
                            : undefined,
                })),
                smallDots: !!singleTimeSeries.origin, // Only external data (e.g. RIWIS) has an origin
                dashedLine: singleTimeSeries.stats_type === 'average',
                color,
            }
        })
    }

    const mapValueTypeToUnit = (valueType: MarketDataTimeSeries['value_type']): string | undefined => {
        switch (valueType) {
            case 'percentage':
                return '%'
            case 'monetary_value':
                return '€'
            case 'money_per_area_value':
                return '€/m²'
            case 'float':
            case 'integer':
                return undefined
            default:
                throw ExhaustivenessError(valueType)
        }
    }

    const mapValueToNumber = (valueType: MarketDataTimeSeries['value_type'], value: TimeSeriesValueType): number => {
        switch (valueType) {
            case 'percentage':
                return (value as Percentage) * 100
            case 'monetary_value':
                return MonetaryValue.fromMonetaryValueDataObject(value as MonetaryValueDataObjectType).toDecimal()
            case 'money_per_area_value':
                return (value as MoneyPerAreaValue).amount / 100
            case 'float':
            case 'integer':
                return value as number
            default:
                throw ExhaustivenessError(valueType)
        }
    }

    return {
        mapAssetToMarketDataSeries,
        mapTimeSeriesToChartData,
        mapValueTypeToUnit,
    }
}
